import React, { useState } from "react";
import { motion } from "framer-motion";
import { Globe, BookOpen, Mic, Newspaper, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import api from "@/lib/api";

const BENEFITS = [
  { icon: Globe, title: "Global visibility", body: "Distribution across our print, digital and social surfaces, reaching a considered international readership of leaders and operators." },
  { icon: BookOpen, title: "Magazine feature", body: "Consideration for inclusion in an upcoming print edition, produced with the craft of a traditional editorial masthead." },
  { icon: Mic, title: "Interview opportunity", body: "A long-form, unhurried conversation with our editorial desk, published as part of our interviews series." },
  { icon: Newspaper, title: "News publication", body: "A dispatch from our news desk covering the milestones and developments most relevant to your work." },
];

const PROCESS = [
  { n: "01", t: "Application", d: "Submit the form below with a brief note on your work, your organisation and what you hope to share with our readers." },
  { n: "02", t: "Editorial review", d: "Our editors review each application against our standards. We look for substance, originality and durable relevance." },
  { n: "03", t: "Conversation", d: "Selected applicants are invited to a preliminary editorial conversation to shape the scope of coverage." },
  { n: "04", t: "Publication", d: "The finished piece is edited, verified and published across our surfaces once it meets our editorial bar." },
];

const GetFeaturedPage = () => {
  const [form, setForm] = useState({
    full_name: "", email: "", company: "", role: "", country: "", website: "", interest: "magazine", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.getFeatured(form);
      setDone(true);
      toast.success("Application received. Our editorial desk will be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="get-featured-page">
      {/* Hero */}
      <section className="pt-16 md:pt-24 pb-16 max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="overline mb-6">Get Featured</div>
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="font-display font-normal text-ink text-5xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-[-0.02em] max-w-5xl"
        >
          Bring your work to <span className="italic">a considered</span> readership.
        </motion.h1>
        <p className="mt-8 max-w-2xl text-ink-soft text-lg leading-relaxed">
          Editorial features in World Visionary Times reach an international readership of leaders, founders and operators. Applications are reviewed against our editorial standards.
        </p>
      </section>

      {/* Why get featured */}
      <section className="py-24 md:py-32 bg-cream border-y border-ink-line">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <SectionHeader overline="Why Get Featured" title="What a feature can do for serious work." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="editorial-card p-8"
                data-testid={`benefit-${i}`}
              >
                <b.icon size={22} strokeWidth={1.2} className="text-ink" />
                <div className="mt-6 font-display text-ink text-2xl leading-tight">{b.title}</div>
                <p className="mt-4 text-ink-soft text-sm leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial process */}
      <section className="py-24 md:py-32 max-w-[1440px] mx-auto px-5 md:px-10">
        <SectionHeader overline="Editorial Process" title="From application to publication." />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-6">
          {PROCESS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="p-6 border border-ink-line"
              data-testid={`process-${s.n}`}
            >
              <div className="number-shadow text-5xl leading-none mb-6">{s.n}</div>
              <div className="font-display text-ink text-xl md:text-2xl leading-tight">{s.t}</div>
              <p className="mt-3 text-ink-soft text-sm leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section className="py-24 md:py-32 bg-cream border-y border-ink-line">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <div className="overline mb-6">Apply</div>
              <h2 className="font-display text-ink text-4xl md:text-5xl leading-[1.06]">Tell us who you are and what you would like our readers to know.</h2>
              <p className="mt-6 text-ink-soft text-base leading-relaxed">Applications are reviewed by our editorial desk. You will hear from us within five working days.</p>
            </div>
            <div className="lg:col-span-7">
              {done ? (
                <div className="editorial-card p-10 text-center" data-testid="featured-form-success">
                  <div className="overline">Application Received</div>
                  <div className="mt-4 font-display text-ink text-3xl">Thank you. We{"\u2019"}ll be in touch shortly.</div>
                  <p className="mt-3 text-ink-soft text-sm">Our editorial desk reviews every application personally. Expect to hear from us within five working days.</p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="featured-form" className="space-y-6 editorial-card p-8 md:p-10">
                  <Field label="Full name" required id="ff-name" value={form.full_name} onChange={update("full_name")} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Email" type="email" required id="ff-email" value={form.email} onChange={update("email")} />
                    <Field label="Country" id="ff-country" value={form.country} onChange={update("country")} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Company / Organisation" required id="ff-company" value={form.company} onChange={update("company")} />
                    <Field label="Role / Position" required id="ff-role" value={form.role} onChange={update("role")} />
                  </div>
                  <Field label="Website" id="ff-website" value={form.website} onChange={update("website")} />
                  <div>
                    <label className="overline block mb-3" htmlFor="ff-interest">I{"\u2019"}m interested in</label>
                    <select
                      id="ff-interest"
                      data-testid="featured-interest"
                      value={form.interest}
                      onChange={update("interest")}
                      className="w-full bg-paper border border-ink-line focus:border-ink text-ink text-base py-3 px-4 outline-none appearance-none"
                    >
                      <option value="magazine">Magazine feature</option>
                      <option value="interview">Long-form interview</option>
                      <option value="article">Editorial article</option>
                      <option value="news">News publication</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-3" htmlFor="ff-message">Tell us about your work</label>
                    <textarea
                      id="ff-message"
                      data-testid="featured-message"
                      rows={5}
                      required
                      value={form.message}
                      onChange={update("message")}
                      className="w-full bg-paper border border-ink-line focus:border-ink text-ink text-base py-3 px-4 outline-none resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="featured-submit"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-ink text-paper font-medium text-sm tracking-wide rounded-full hover:bg-gold-dark transition-colors disabled:opacity-40"
                  >
                    {submitting ? "Submitting…" : "Apply Now"} <ArrowRight size={16} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, required, ...props }) => (
  <div>
    <label className="overline block mb-3" htmlFor={props.id}>{label}{required && <span className="text-gold-dark ml-0.5">*</span>}</label>
    <input
      required={required}
      data-testid={`field-${props.id}`}
      {...props}
      className="w-full bg-transparent border-b border-ink-line focus:border-ink text-ink text-base py-3 outline-none transition-colors"
    />
  </div>
);

export default GetFeaturedPage;
